import { createSlice } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import { APP_STATUS } from '../../constants';
import { IGuardianState } from '../../interface/guardian.interface';
import {
  changePrimaryGuardian,
  createGuardian,
  deleteGuardian,
  getGuardians,
  getGuardianTypes,
  linkGuardianToStudent,
  removeGuardian,
  sendGuardianInvitation,
  updateGuardian,
} from '../action';

const initialState: IGuardianState = {
  isLoading: false,
  isGuardianLoading: false,
  isTypesLoading: false,
  status: APP_STATUS.IDLE,
  message: '',
  guardians: [],
  allGuardians: [],
  relationshipTypes: [],
  pagination: null,
};

const guardianSlice = createSlice({
  name: 'guardian',
  initialState,
  reducers: {
    resetGuardianState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Get all Guardians
      .addCase(getGuardians.pending, (state) => {
        state.isGuardianLoading = true;
        state.status = APP_STATUS.LOADING;
        state.allGuardians = [];
        state.pagination = null;
      })
      .addCase(getGuardians.fulfilled, (state, action) => {
        state.isGuardianLoading = false;
        state.status = APP_STATUS.SUCCESS;
        state.allGuardians = action.payload.data.guardians;
        state.pagination = {
          total: action.payload.data.total,
          page: action.payload.data.page,
          pageSize: action.payload.data.pageSize,
        };
      })
      .addCase(getGuardians.rejected, (state, action) => {
        state.isGuardianLoading = false;
        state.status = APP_STATUS.ERROR;
        state.message = action.payload?.message || i18n.t('toast.failed_to_fetch_guardians', 'Failed to fetch guardians');
        state.allGuardians = [];
        state.pagination = null;
      })

      // Create Guardian
      .addCase(createGuardian.pending, (state) => {
        state.isLoading = true;
        state.status = APP_STATUS.LOADING;
      })
      .addCase(createGuardian.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = APP_STATUS.SUCCESS;
        state.message = action.payload.message;
      })
      .addCase(createGuardian.rejected, (state, action) => {
        state.isLoading = false;
        state.status = APP_STATUS.ERROR;
        state.message = action.payload?.message || i18n.t('toast.failed_to_create_guardian', 'Failed to create guardian');
      })

      // Update Guardian
      .addCase(updateGuardian.pending, (state) => {
        state.isLoading = true;
        state.status = APP_STATUS.LOADING;
      })
      .addCase(updateGuardian.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = APP_STATUS.SUCCESS;
        state.message = action.payload.message;
      })
      .addCase(updateGuardian.rejected, (state, action) => {
        state.isLoading = false;
        state.status = APP_STATUS.ERROR;
        state.message = action.payload?.message || i18n.t('toast.failed_to_update_guardian', 'Failed to update guardian');
      })

      // Delete Guardian
      .addCase(deleteGuardian.pending, (state) => {
        state.isLoading = true;
        state.status = APP_STATUS.LOADING;
      })
      .addCase(deleteGuardian.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = APP_STATUS.SUCCESS;
        state.message = action.payload.message;
      })
      .addCase(deleteGuardian.rejected, (state, action) => {
        state.isLoading = false;
        state.status = APP_STATUS.ERROR;
        state.message = action.payload?.message || i18n.t('toast.failed_to_delete_guardian', 'Failed to delete guardian');
      })

      // Remove Guardian
      .addCase(removeGuardian.pending, (state) => {
        state.isLoading = true;
        state.status = APP_STATUS.LOADING;
      })
      .addCase(removeGuardian.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = APP_STATUS.SUCCESS;
        state.message = action.payload.message;
      })
      .addCase(removeGuardian.rejected, (state, action) => {
        state.isLoading = false;
        state.status = APP_STATUS.ERROR;
        state.message = action.payload?.message || i18n.t('toast.failed_to_remove_guardian', 'Failed to remove guardian');
      })

      // Change Primary Guardian
      .addCase(changePrimaryGuardian.pending, (state) => {
        state.isLoading = true;
        state.status = APP_STATUS.LOADING;
      })
      .addCase(changePrimaryGuardian.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = APP_STATUS.SUCCESS;
        state.message = action.payload.message;
      })
      .addCase(changePrimaryGuardian.rejected, (state, action) => {
        state.isLoading = false;
        state.status = APP_STATUS.ERROR;
        state.message = action.payload?.message || i18n.t('toast.failed_to_change_primary_guardian', 'Failed to change primary guardian');
      })

      // Get Guardian Types
      .addCase(getGuardianTypes.pending, (state) => {
        state.isTypesLoading = true;
      })
      .addCase(getGuardianTypes.fulfilled, (state, action) => {
        state.isTypesLoading = false;
        state.relationshipTypes = action.payload.data.types;
      })
      .addCase(getGuardianTypes.rejected, (state) => {
        state.isTypesLoading = false;
        state.relationshipTypes = [];
      })

      // Link Guardian to Student
      .addCase(linkGuardianToStudent.pending, (state) => {
        state.isLoading = true;
        state.status = APP_STATUS.LOADING;
      })
      .addCase(linkGuardianToStudent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = APP_STATUS.SUCCESS;
        state.message = action.payload.message;
      })
      .addCase(linkGuardianToStudent.rejected, (state, action) => {
        state.isLoading = false;
        state.status = APP_STATUS.ERROR;
        state.message = action.payload?.message || i18n.t('toast.failed_to_link_guardian', 'Failed to link guardian');
      })

      // Send Guardian Invitation (send / resend)
      .addCase(sendGuardianInvitation.pending, (state) => {
        state.isLoading = true;
        state.status = APP_STATUS.LOADING;
      })
      .addCase(sendGuardianInvitation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = APP_STATUS.SUCCESS;
        state.message = action.payload.message;
      })
      .addCase(sendGuardianInvitation.rejected, (state, action) => {
        state.isLoading = false;
        state.status = APP_STATUS.ERROR;
        state.message = action.payload?.message || i18n.t('toast.failed_to_send_invitations', 'Failed to send invitations');
      });
  },
});

export const { resetGuardianState } = guardianSlice.actions;

export default guardianSlice.reducer;
